/**
 * mobile-channels.ts — Logic thuần của hàng "tài khoản kênh" ở đầu Hộp thư trên ĐIỆN THOẠI (PWA): thứ tự người dùng tự xếp,
 * kênh nào hiện ở hàng (cố định 6 ô: "Tất cả" + 4 kênh đầu + nút mở rộng), và bản lưu đồng bộ nhiều thiết bị
 * (hooks/use-mobile-channels.ts ↔ backend `me/ui-prefs.ts`, khoá `mobile-channels`).
 * Giao diện: pages/conversations/mobile-inbox-header.tsx (hàng) + mobile-channels-sheet.tsx (danh sách đầy đủ + sắp xếp).
 */
import { moveTo } from '@/lib/reorder-drag'

/** Hàng ở đầu trang luôn có tối đa bấy nhiêu ô: "Tất cả" + các kênh + nút mở rộng ⇒ vừa một hàng điện thoại, không cuộn ngang. */
export const ROW_CELLS = 6
/** Số kênh hiện ở hàng khi có nhiều kênh: 6 ô − "Tất cả" − nút mở rộng. */
export const ROW_CHANNELS = ROW_CELLS - 2
/** Từ bấy nhiêu kênh trở lên mới có nút mở rộng (một kênh thì không có gì để sắp xếp). */
export const EXPAND_FROM = 2

/** Số mã kênh tối đa lưu — khớp backend `MAX_MOBILE_CHANNELS`. */
export const MAX_SAVED_CHANNELS = 200

/**
 * Sắp tài khoản theo thứ tự người dùng đã lưu (`order` = mã tài khoản). Tài khoản chưa có trong `order` (kênh mới kết nối)
 * đứng SAU các tài khoản đã xếp, giữ thứ tự gốc; mã đã xoá trong `order` bị bỏ qua. `order` rỗng → giữ nguyên thứ tự gốc.
 */
export function orderAccounts<T extends { id: string }>(accounts: T[], order: readonly string[]): T[] {
  if (order.length === 0) return accounts
  const rank = new Map<string, number>()
  order.forEach((id, i) => { if (!rank.has(id)) rank.set(id, i) })
  const known = accounts.filter((a) => rank.has(a.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!)
  const fresh = accounts.filter((a) => !rank.has(a.id))
  return [...known, ...fresh]
}

/** Có nút mở rộng không (từ `EXPAND_FROM` kênh trở lên). */
export const hasExpand = (total: number) => total >= EXPAND_FROM

/**
 * Các kênh hiện ở hàng đầu trang. Vừa chỗ → hiện hết; nhiều hơn → `ROW_CHANNELS` kênh đầu theo thứ tự đã xếp.
 * Đang lọc theo một kênh nằm ngoài các ô đầu (chọn từ danh sách mở rộng) → kênh đó thế vào ô kênh cuối của hàng để người dùng
 * thấy mình đang lọc theo kênh nào (chỉ thay cách HIỂN THỊ, thứ tự đã lưu không đổi).
 */
export function visibleAccounts<T extends { id: string }>(ordered: T[], activeId?: string, max = ROW_CHANNELS): T[] {
  if (ordered.length <= max) return ordered
  const head = ordered.slice(0, max)
  if (!activeId || head.some((a) => a.id === activeId)) return head
  const active = ordered.find((a) => a.id === activeId)
  return active ? [...head.slice(0, max - 1), active] : head
}

/** Dời một kênh lên (-1) / xuống (+1) một bậc trong danh sách mã; ở mép → không đổi (trả lại chính mảng). */
export function moveAccountId(ids: string[], id: string, dir: -1 | 1): string[] {
  const i = ids.indexOf(id)
  return i < 0 ? ids : moveTo(ids, i, i + dir)
}

// ── Bản lưu: bộ nhớ đệm trên máy + đồng bộ theo tài khoản ─────────────────────

/** Thứ tự mã tài khoản + thời điểm người dùng sửa (ISO) — quyết định bản nào mới hơn giữa các thiết bị. Rỗng = thứ tự mặc định. */
export interface ChannelsPref { channels: string[]; updatedAt: string }

export const channelsStorageKey = (userId?: string | null) => `ecdp_mobile_channels:${userId || 'anon'}`

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const stampOf = (iso: unknown): number => (typeof iso === 'string' ? Date.parse(iso) : NaN)

/** Làm sạch danh sách mã: chỉ giữ chuỗi 1–64 ký tự, bỏ trùng, giữ thứ tự, cắt ở `MAX_SAVED_CHANNELS`. */
export function normalizeChannelIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const x of raw) if (typeof x === 'string' && x.length > 0 && x.length <= 64 && !out.includes(x)) out.push(x)
  return out.slice(0, MAX_SAVED_CHANNELS)
}

/** Làm sạch bản từ máy chủ / bộ nhớ: phải có mảng + giờ đọc được (mảng rỗng vẫn hợp lệ). Hỏng → null ("không có bản nào"). */
export function parseChannelsPref(raw: unknown): ChannelsPref | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const { channels, updatedAt } = raw as { channels?: unknown; updatedAt?: unknown }
  if (!Array.isArray(channels) || !Number.isFinite(stampOf(updatedAt))) return null
  return { channels: normalizeChannelIds(channels), updatedAt: new Date(stampOf(updatedAt)).toISOString() }
}

export type ChannelsMergeAction = 'none' | 'adopt-remote' | 'push-local'

export const sameChannels = (a: ChannelsPref, b: ChannelsPref) => a.channels.length === b.channels.length && a.channels.every((x, i) => x === b.channels[i])

/**
 * Hợp nhất bản trên máy (`local`, null = chưa từng xếp) với bản máy chủ (`remote`, null = máy chủ chưa có):
 * cả hai trống → không làm gì; máy chủ trống mà máy này có → đẩy lên; máy này trống → nhận bản máy chủ;
 * cả hai có → `updatedAt` mới hơn thắng; bằng nhau mà khác nội dung → theo máy chủ (một nguồn sự thật).
 */
export function mergeChannelsPref(local: ChannelsPref | null, remote: ChannelsPref | null): { pref: ChannelsPref | null; action: ChannelsMergeAction } {
  if (!remote) return local ? { pref: local, action: 'push-local' } : { pref: null, action: 'none' }
  if (!local) return { pref: remote, action: 'adopt-remote' }
  const tl = stampOf(local.updatedAt)
  const tr = stampOf(remote.updatedAt)
  if (tr > tl) return { pref: remote, action: 'adopt-remote' }
  if (tl > tr) return { pref: local, action: 'push-local' }
  return sameChannels(local, remote) ? { pref: local, action: 'none' } : { pref: remote, action: 'adopt-remote' }
}

export function loadChannelsPref(userId: string | null | undefined, store?: KeyValueStore): ChannelsPref | null {
  try {
    return parseChannelsPref(JSON.parse((store ?? localStorage).getItem(channelsStorageKey(userId)) ?? 'null'))
  } catch {
    return null
  }
}

export function saveChannelsPref(userId: string | null | undefined, pref: ChannelsPref, store?: KeyValueStore): void {
  try { (store ?? localStorage).setItem(channelsStorageKey(userId), JSON.stringify(pref)) } catch { /* bộ nhớ bị chặn / đầy → vẫn dùng được trong phiên */ }
}
